/** @format */

import React, { useCallback, useEffect } from "react";
import qs from "qs";
import { useLocation, useParams } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { createUseStyles } from "react-jss";
import { ThemeType } from "../styles/theme";
import { withVersionParams } from "../HOC/withVersionParams";
import { Data } from "../types";
import { tosDataHandler } from "../constants/TosData";

interface ParamTypes {
   [tosId: string]: string | undefined;
}

interface Props {
   tosDataHandler: (tosIdx: number, version: number) => Data;
   v: number;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
   wrap: {
      width: "100%",
      padding: `30px ${theme.gap}px`,
   },
   desc: {
      "& .divider": {
         width: "100%",
         height: 1,
         marginTop: 26,
         borderBottom: `1px solid ${theme.monoDivider}`,
      },
      "& h1": {
         fontSize: "2.2rem",
         fontWeight: theme.demiLight,
         marginTop: 60,
         marginBottom: 20,
         "&:first-of-type": {
            marginTop: 0,
            marginBottom: 0,
         },
      },
      "& h2": {
         fontSize: "1.8rem",
         paddingTop: 30,
         fontWeight: theme.demiLight,
         "&:first-of-type": {
            paddingTop: 26,
         },
      },
      "& p": {
         color: theme.mono50,
         paddingTop: 10,
         "&.version": {
            paddingTop: 80,
         },
      },
      "& > ol:not(.sub)": {
         margin: 0,
         listStyle: "none",
         paddingLeft: 0,
         paddingTop: 6,
      },
      "& > ol:not(.sub) > li": {
         color: theme.mono50,
         marginTop: 8,
         "&:first-of-type": {
            marginTop: 0,
         },
         "& > ol.sub": {
            marginBottom: "16px",
         },
         "&:last-of-type > ol.sub": {
            marginBottom: "0px",
         },
      },
      "& ol.sub": {
         margin: 0,
         listStyle: "decimal",
         marginTop: 16,
         marginLeft: 14,
         paddingLeft: 14,
      },
      "& ol.sub > li": {
         color: theme.mono50,
         marginTop: 8,
         "&:first-of-type": {
            marginTop: 0,
         },
      },
      "& table": {
         width: "100%",
         color: theme.mono50,
         marginTop: 14,
         marginBottom: 14,
         fontSize: "1.4rem",
         lineHeight: "2.2rem",
         borderCollapse: "collapse",
         "& ol": {
            listStyle: "none",
            padding: 0,
            margin: 0,
         },
         "& tr": {
            "& td": {
               padding: 8,
               verticalAlign: "baseline",
               borderBottom: `1px solid ${theme.monoDim}`,
               borderRight: `1px solid ${theme.monoDim}`,
               "&:first-of-type": {
                  borderLeft: `1px solid ${theme.monoDim}`,
               },
            },
            "&:first-of-type": {
               border: 0,
               background: theme.monoDim,
               color: theme.mono100,
               textAlign: "center",
               "& td": {
                  padding: "8px",
                  borderRight: `1px solid ${theme.mono100}`,
                  "&:last-of-type": {
                     borderRight: `1px solid ${theme.monoDim}`,
                  },
               },
            },
         },
      },
      "& .mg-26": {
         marginBottom: 26,
      },
      "& .numbering": {
         ...theme.text,
         lineHeight: "2.6rem",
         color: theme.mono50,
         marginBottom: 20,
      },
      "& div.str": {
         color: theme.primaryTxt,
         fontWeight: theme.medium,
      },
   },
}));

function hashLinkScroll(location: any) {
   const { search } = location;
   const { to } = qs.parse(search, { ignoreQueryPrefix: true });
   if (!!to) {
      setTimeout(() => {
         const element = document.getElementById(to as string);
         if (!!element) element.scrollIntoView();
      }, 300);
   }
}

const TosDetailPage: React.FC = () => {
   const v = 10601;
   const classes = useStyles();
   const location = useLocation();
   const { tosId } = useParams<ParamTypes>();
   const item = tosDataHandler(Number(tosId), v);

   const BROOF_TITLE_IS_POLICY = "broof 서비스 이용약관";
   const BROOF_TITLE_IS_PRIVACY = "broof 개인정보 수집 및 이용 동의";

   const includeParameter = useCallback(
      (param: string) => {
         return location.search.includes(param);
      },
      [location]
   );

   useEffect(() => {
      hashLinkScroll(location);
   }, [location]);

   return (
      <div className={classes.wrap}>
         <HelmetProvider>
            <Helmet>
               <title>
                  {includeParameter("id=policy")
                     ? BROOF_TITLE_IS_POLICY
                     : includeParameter("id=privacy")
                     ? BROOF_TITLE_IS_PRIVACY
                     : item.title}
               </title>
            </Helmet>
         </HelmetProvider>
         <div
            className={classes.desc}
            dangerouslySetInnerHTML={{
               __html: item.desc,
            }}
         ></div>
      </div>
   );
};

export default withVersionParams(TosDetailPage);
